import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight } from '@phosphor-icons/react'
import { useActiveAnalysis } from '../contexts/ActiveAnalysisContext'
import OrbitRing from './OrbitRing'
import { ACTIVITY_CONFIGS, FLASH_OVERRIDES } from '../config/activityTypes'
import type { ActivityKind } from '../config/activityTypes'

const SPRING = { type: 'spring' as const, stiffness: 400, damping: 30 }

function getActivityConfig(analysis: { kind?: ActivityKind; type?: string }) {
  const base = ACTIVITY_CONFIGS[analysis.kind ?? 'analysis']
  if (analysis.kind === 'analysis' && analysis.type === 'flash') {
    return { ...base, ...FLASH_OVERRIDES }
  }
  return base
}

function getProgressMessage(startedAt: number, analysis: { kind?: ActivityKind; type?: string }): string {
  const config = getActivityConfig(analysis)
  const elapsed = Math.floor((Date.now() - startedAt) / 1000)
  let msg = config.progressMessages[0]?.text ?? 'En cours...'
  for (const m of config.progressMessages) {
    if (elapsed >= m.delay) msg = m.text
  }
  return msg
}

function formatElapsed(startedAt: number): string {
  const elapsed = Math.floor((Date.now() - startedAt) / 1000)
  const min = Math.floor(elapsed / 60)
  const sec = elapsed % 60
  return `${min}:${sec.toString().padStart(2, '0')}`
}

function getProgress(startedAt: number, analysis: { kind?: ActivityKind; type?: string }): number {
  const config = getActivityConfig(analysis)
  if (config.estimatedDuration === 0) return 0
  const elapsed = (Date.now() - startedAt) / 1000
  return Math.min(88, (elapsed / config.estimatedDuration) * 88)
}

function useIsDesktop() {
  const [d, setD] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  )
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)')
    const handler = (e: MediaQueryListEvent) => setD(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])
  return d
}

export default function DynamicIsland() {
  const { analyses, completedAnalysis, dismissAnalysis, dismissCompleted } = useActiveAnalysis()
  const isDesktop = useIsDesktop()
  const [expanded, setExpanded] = useState(false)
  const [, setTick] = useState(0)
  const islandRef = useRef<HTMLDivElement>(null)
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevCountRef = useRef(0)

  const processing = analyses.filter(a => a.status === 'processing')
  const active = processing[0]
  const completed = completedAnalysis
  const isVisible = !!active || !!completed
  // Processing takes priority — new analysis must show over stale completion
  const isCompleted = !active && !!completed
  const currentAnalysis = active || completed
  const processingCount = processing.length

  // Tick every second for timer + ring progress
  useEffect(() => {
    if (!active) return
    const timer = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(timer)
  }, [active])

  // Centralized expand with auto-collapse
  const expandBriefly = useCallback((duration: number) => {
    setExpanded(true)
    if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current)
    collapseTimerRef.current = setTimeout(() => setExpanded(false), duration)
  }, [])

  // Auto-expand only when count INCREASES (new analysis submitted)
  // Decrease = completion → handled by the completion effect with longer duration
  useEffect(() => {
    if (processingCount > prevCountRef.current && processingCount > 0) {
      const t = setTimeout(() => expandBriefly(3000), 0)
      prevCountRef.current = processingCount
      return () => clearTimeout(t)
    }
    prevCountRef.current = processingCount
  }, [processingCount, expandBriefly])

  // Auto-expand on completion (longer duration — 8s)
  useEffect(() => {
    if (!completed) return
    const t = setTimeout(() => expandBriefly(8000), 0)
    return () => clearTimeout(t)
  }, [completed, expandBriefly])

  // Click outside to collapse
  useEffect(() => {
    if (!expanded) return
    const handler = (e: MouseEvent) => {
      if (islandRef.current && !islandRef.current.contains(e.target as Node)) {
        setExpanded(false)
        if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [expanded])

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current) }
  }, [])

  const handleClick = () => {
    if (isCompleted && currentAnalysis) {
      dismissAnalysis(currentAnalysis.id)
      dismissCompleted()
      window.location.assign(`/result?id=${currentAnalysis.id}`)
    } else {
      setExpanded(prev => !prev)
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current)
    }
  }

  if (!currentAnalysis) return null

  const config = getActivityConfig(currentAnalysis)
  const progress = isCompleted ? 100 : getProgress(currentAnalysis.startedAt, currentAnalysis)
  const typeLabel = currentAnalysis.type === 'flash' ? 'Flash'
    : currentAnalysis.type === 'approfondi' ? 'Approfondi'
    : config.label

  // ═══════════════════════════════════════════════
  // UNIFIED PILL — same compact design for mobile & desktop
  // Mobile: fixed centered below navbar
  // Desktop: inline in navbar center
  // ═══════════════════════════════════════════════
  const pill = (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={islandRef}
          key="di-pill"
          layout
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{
            scale: 1,
            opacity: 1,
            background: isCompleted ? config.completedGradient : config.processingGradient,
            boxShadow: isCompleted ? config.completedShadow : config.processingShadow,
          }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{
            layout: SPRING,
            scale: { type: 'spring', stiffness: 500, damping: 30 },
            opacity: { duration: 0.2 },
            background: { duration: 0.6, ease: 'easeInOut' },
            boxShadow: { duration: 0.6, ease: 'easeInOut' },
          }}
          role="button"
          tabIndex={0}
          aria-label={isCompleted
            ? `${config.label} ${typeLabel} terminée. Appuyer pour voir le résultat.`
            : `${processingCount} ${processingCount > 1 ? 'activités' : 'activité'} en cours`}
          onClick={handleClick}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick() } }}
          whileTap={{ scale: 0.97 }}
          onMouseEnter={() => isDesktop && !isCompleted && setExpanded(true)}
          onMouseLeave={() => {
            if (isDesktop && !isCompleted) {
              setExpanded(false)
              if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current)
            }
          }}
          className={`relative cursor-pointer select-none ${
            expanded ? 'px-4 py-2.5 min-w-[260px]' : 'px-3 py-1.5 h-[36px]'
          }`}
          style={{
            borderRadius: expanded ? 18 : 22,
            overflow: 'hidden',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {/* Ambient glow */}
          <motion.div
            className="absolute -inset-1 rounded-[28px] opacity-50 pointer-events-none -z-10"
            animate={{
              boxShadow: isCompleted
                ? [
                    '0 0 15px 2px rgba(16,185,129,0.08)',
                    '0 0 20px 4px rgba(16,185,129,0.15)',
                    '0 0 15px 2px rgba(16,185,129,0.08)',
                  ]
                : [
                    `0 0 15px 2px ${config.glowColor}0.08)`,
                    `0 0 20px 4px ${config.glowColor}0.15)`,
                    `0 0 15px 2px ${config.glowColor}0.08)`,
                  ],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Shimmer sweep */}
          {!isCompleted && config.shimmerEnabled && (
            <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: 'inherit' }}>
              <motion.div
                className="absolute inset-y-0 w-[200%] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
              />
            </div>
          )}

          {/* Content — compact / expanded */}
          <AnimatePresence mode="popLayout" initial={false}>
            {!expanded ? (
              /* ─── COMPACT ─── */
              <motion.div
                key="compact"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="relative flex items-center gap-2.5"
              >
                <OrbitRing
                  size={24}
                  progress={progress}
                  isProcessing={!isCompleted}
                  isCompleted={isCompleted}
                  score={currentAnalysis.scoreGlobal}
                  processingColor={config.processingColor}
                />
                {isCompleted ? (
                  <span className="text-xs font-medium text-emerald-300 whitespace-nowrap">Prête !</span>
                ) : (
                  <>
                    <span className="text-xs font-mono text-white/90 tabular-nums whitespace-nowrap">
                      {formatElapsed(currentAnalysis.startedAt)}
                    </span>
                    {processingCount > 1 && (
                      <span className="flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[var(--bn-accent-bg-active)] border border-[var(--bn-accent-border)] text-[9px] font-bold text-[var(--bn-accent)] tabular-nums">
                        {processingCount}
                      </span>
                    )}
                  </>
                )}
              </motion.div>
            ) : (
              /* ─── EXPANDED ─── */
              <motion.div
                key="expanded"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="relative"
              >
                <div className="flex items-center gap-3 mb-2">
                  <OrbitRing
                    size={32}
                    progress={progress}
                    isProcessing={!isCompleted}
                    isCompleted={isCompleted}
                    score={currentAnalysis.scoreGlobal}
                    processingColor={config.processingColor}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold leading-tight ${isCompleted ? 'text-emerald-300' : 'text-white/90'}`}>
                      {isCompleted
                        ? `Analyse ${typeLabel} terminée !`
                        : processingCount > 1
                          ? `${processingCount} analyses en cours`
                          : `Analyse ${typeLabel} en cours`}
                    </p>
                    <p className="text-[10px] text-white/40 mt-0.5 truncate">
                      {isCompleted
                        ? 'Appuie pour voir le résultat'
                        : processingCount > 1
                          ? `${processingCount} en traitement simultané`
                          : getProgressMessage(currentAnalysis.startedAt, currentAnalysis)}
                    </p>
                  </div>
                  {isCompleted ? (
                    <div className="flex items-center gap-1 text-emerald-400 shrink-0">
                      <span className="text-xs font-medium">Voir</span>
                      <ArrowRight className="w-3.5 h-3.5" weight="bold" />
                    </div>
                  ) : (
                    <span className="text-sm font-mono text-[var(--bn-accent)] tabular-nums shrink-0">
                      {formatElapsed(currentAnalysis.startedAt)}
                    </span>
                  )}
                </div>
                {!isCompleted ? (
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[var(--bn-accent)] rounded-full"
                      initial={{ width: '5%' }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 2, ease: 'easeOut' }}
                    />
                  </div>
                ) : (
                  <div className="h-1 bg-emerald-500/20 rounded-full overflow-hidden">
                    <div className="h-full w-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" />
                  </div>
                )}
                {processingCount > 1 && (
                  <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5">
                    {processing.map(a => {
                      const cfg = getActivityConfig(a)
                      return (
                        <motion.div
                          key={a.id}
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2"
                        >
                          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.processingColor }} />
                          <span className="text-[10px] text-white/60 flex-1 truncate">
                            {cfg.label} {a.type === 'flash' ? '(Flash)' : a.type === 'approfondi' ? '(Approfondi)' : ''}
                          </span>
                          <span className="text-[10px] font-mono text-white/40 tabular-nums shrink-0">
                            {formatElapsed(a.startedAt)}
                          </span>
                        </motion.div>
                      )
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  )

  // Mobile: fixed centered pill below navbar
  if (!isDesktop) {
    return (
      <div className="fixed top-[calc(3.5rem+0.5rem)] z-30 left-0 right-0 flex justify-center pointer-events-none">
        <div className="pointer-events-auto">
          {pill}
        </div>
      </div>
    )
  }

  // Desktop: inline in navbar center (positioned by Navbar.tsx)
  return pill
}
